import {
  DEFAULT_CARD_DECK,
  DEFAULT_TURN_TIMER_SEC,
  TURN_TIMER_OPTIONS,
  type BackgammonSettings,
  type CardDeckId,
  type ChessSettings,
  type DoozPieceColor,
} from "@bing/shared";

/**
 * Host-controlled private tables — shared, framework-free helpers.
 *
 * A private table is a host-created Colyseus room friends join by id (vs. the
 * public quick-match pool). The host picks the rules up-front (`TableSettings`),
 * shares an invite deep-link, and starts the game when ready. These types +
 * pure helpers are consumed by the connector, the store, the settings screen and
 * the waiting room, so they live apart from any I/O or React.
 */

/** Host-configurable rules for a private table (mirrors the server's TableSettings).
 * `turnTimerSec` stays a plain number here (lobby payloads echo whatever the server
 * holds); the CHOICES a host may pick are the shared {@link TURN_TIMER_OPTIONS},
 * which the server's `tableSettingsSchema` enforces on every settings payload. */
export interface TableSettings {
  /** Per-turn time limit in seconds — one of the shared `TURN_TIMER_OPTIONS`. */
  turnTimerSec: number;
  /** The OWNER mutes the whole table: all game sound off for every seated player. */
  muted: boolean;
  /** Whether the table chat is available to the players. */
  chatAllowed: boolean;
  /**
   * حکم ONLY — rounds needed to win the MATCH (3/5/7), host-chosen on a PRIVATE table.
   *
   * NOT to be confused with `backgammon.matchLength`, which is nested inside that game's own block;
   * this one is top-level because the server reads it that way. The server strips it for every
   * other game AND for public quick-match tables (judged by the trusted room game), so leaving it
   * set while switching games is harmless — it simply never reaches the engine.
   */
  matchLength?: number;
  /**
   * حکم ONLY — does a 7–0 sweep pay a کوت under the three SUIT-LESS calls (سرس/نرس/آس نرس)?
   * A host choice on a PRIVATE table; a suit call always pays and ignores it.
   *
   * OPTIONAL, and ABSENT MEANS OFF — the conventional reading and the shipped default, so a
   * table that never chooses is unchanged. Every reader therefore tests `=== true` rather than
   * truthiness: a crafted or stale value can only ever fail to turn it on.
   *
   * Top-level, like {@link matchLength}, because that is how the server reads it. It is STRIPPED
   * for any other game and FORCED to the default on a PUBLIC quick-match table — judged by the
   * trusted room game, in `TableController.normalizeSettings` — so leaving it set while switching
   * games is harmless: it simply never reaches the engine.
   */
  hokmModeKot?: boolean;
  /**
   * حکم ONLY — may the Hakem call one of the three SUIT-LESS modes (سرس / نرس / آس نرس)?
   *
   * Top-level, like {@link matchLength} and {@link hokmModeKot}, because that is how the server
   * reads it. STRIPPED for every other game and FORCED from the ROOM TYPE on a public table, so
   * this only ever means anything on a private host's own table. DEFAULT OFF — the standard game.
   */
  hokmModes?: boolean;
  /**
   * شلم ONLY — the points that win the match: ۶۶۰ or ۱۲۰۰ (`SHELEM_TARGETS`), a PRIVATE host's pick,
   * default ۶۶۰ (card-67). Top-level, like {@link matchLength}, because that is how the server reads
   * it. The server STRIPS it for every other game, DEFAULTS it on a private شلم table that sent none
   * (so the lobby echo always carries one) and FORCES ۶۶۰ on a public one — judged by the trusted
   * room game.
   */
  shelemTarget?: number;
  /**
   * دوز ONLY — the colour SEAT 0 plays in; the other seat takes the remaining one.
   *
   * ⚠️ A TABLE FACT, NOT A DEVICE PREFERENCE. It used to be viewer-side, so two players could look
   * at the same cell and name two different colours. The private HOST picks it (they are seat 0)
   * and it rides the lobby echo to both clients; the server FORCES the default on a public table,
   * where there is nobody to choose for the stranger who joins, and STRIPS it for every other game
   * (judged by the trusted room game, in `TableController.normalizeSettings`).
   *
   * ⚠️ THE TWO DECLARATIONS OF THIS FRAME ARE HAND-WRITTEN AND NOTHING KEEPS THEM IN STEP — this
   * one and the server's `tableSettingsSchema`. Add a key to either and add it to BOTH. (The last
   * time they drifted, `LobbyState.phase` omitted a value the server really sends.)
   */
  doozColor?: DoozPieceColor;
  /** The card-back skin every seated player sees at this table. */
  deck: CardDeckId;
  /** CHESS ONLY — the host's chess table choices (colour / time control / disconnect).
   *  A card table never sets it; it rides through create + the lobby echo untouched. */
  chess?: ChessSettings;
  /** BACKGAMMON ONLY — the host's تخته نرد table choices. Same story as {@link chess}:
   *  card + chess tables never set it, and the server strips a block that isn't the
   *  table's own (`normalizeSettings`, keyed on the trusted room game). */
  backgammon?: BackgammonSettings;
}

/** The pre-selected settings a host sees on the Game Settings screen. */
export const DEFAULT_TABLE_SETTINGS: TableSettings = {
  turnTimerSec: DEFAULT_TURN_TIMER_SEC,
  muted: false,
  chatAllowed: true,
  deck: DEFAULT_CARD_DECK,
};

/** Turn-timer chip choices on the settings screen — the shared allowed set
 * (15/25/35/45/60s, default 25; there is no "off" — every table runs a clock). */
export { TURN_TIMER_OPTIONS };

/**
 * How the online container should acquire its room:
 * - `quick`  — public quick-match (`joinOrCreate`), unchanged from before.
 * - `create` — host creates a private room with `settings` (`client.create`).
 * - `join`   — a friend joins an existing private room by id (`client.joinById`).
 */
export type OnlineJoin =
  | { readonly kind: "quick" }
  | {
      readonly kind: "create";
      readonly settings: TableSettings;
      /**
       * THE TABLE'S SIZE, for a game whose sizes are separate ROOM TYPES — پاسور's ۲ or ۴ (operator,
       * 2026-10-20), picked on «تنظیمات میز». NOT a setting and NEVER on the wire: a room's size is
       * fixed when it is defined, so the game's container names the room from it
       * (`room-sizes.ts`) and the connector sends `settings` alone. Absent ⇒ the game's default
       * size (a friend invited from the list).
       */
      readonly seats?: number;
    }
  /** Join a table by id (an accepted invite / a `t_<game>_<roomId>` deep link). `preferredSeat`
   *  is the chair the inviter tapped the «+» on — a PREFERENCE the server honours only while it is
   *  still free; otherwise the joiner takes the lowest free seat. Never reserved. */
  | {
      readonly kind: "join";
      readonly roomId: string;
      readonly preferredSeat?: number;
      /** This join came from a table LINK (the Telegram PV button / a copied link), which cannot
       *  carry a seat. The server then reads the invited seat off the invite itself — one bounded
       *  lookup, and ONLY for link joins, so quick-match is untouched. */
      readonly fromLink?: boolean;
    }
  /** RE-JOIN a live game we were in (reclaim our seat from the bot) — reconnect with the saved
   *  Colyseus token instead of a fresh join (a locked, in-progress room refuses `joinById`). */
  | {
      readonly kind: "rejoin";
      readonly roomId: string;
      /**
       * THE SEAT'S TICKET on this device — or `null`: THIS DEVICE HOLDS NO KEY (card-9f). The game-server
       * named the table itself (the «بازی عمومی» refusal's `askHeldSeat`), so the return asks its held-seat
       * door (`takeSeatBack`, origin `held`) with this launch's initData instead of reconnecting by token.
       */
      readonly token: string | null;
      /**
       * STRAIGHT BACK (card-a5 item 1): nobody tapped anything — the app came here by itself on open,
       * because this device holds a seat at this PUBLIC table (`"table"`, a game in progress) or in
       * this public waiting room (`"waiting"`). A return that fails is SILENT then: the app opens
       * where it would have opened, with no error screen. Absent on every tapped return.
       */
      readonly auto?: "table" | "waiting";
      /**
       * AN ACT ELSEWHERE, ROUTED THROUGH THIS TABLE (card-a5 item 1): the player started or joined
       * another game while this device held a seat at this PUBLIC table in progress. The app came back
       * here first so the act is taken with the table's own «خروج» confirmation in front of it — and,
       * once confirmed, charged exactly as «خروج» — then does it. A return that fails (the seat is gone)
       * simply does it: nothing was there to leave.
       */
      readonly then?: ExitIntent;
    }
  /** RESUME a persisted ONLINE chess game (DB-backed, cross-device) — `joinOrCreate` with the
   *  stable `onlineGameId` as the `filterBy(["onlineGameId"])` value, so BOTH players land in the
   *  ONE rehydrated room. Used from the «ادامه بازی» strip; distinct from same-device `rejoin`. */
  | { readonly kind: "resume"; readonly onlineGameId: string };

/**
 * WHAT THE PLAYER WAS ABOUT TO DO when the app routed them through their held public table first
 * (card-a5 item 1) — a plain, serialisable description of the act, applied by the session's
 * `runExitIntent` once the table has been left (or could not be reached).
 */
export type ExitIntent =
  /** Start an offline game — or resume the named save of one (`resumeId`, the «ادامه بازی» strip). */
  | { readonly kind: "solo"; readonly game: string; readonly resumeId?: string }
  /** Host a private table with these rules — at this size, when the game has sizes (see `OnlineJoin`). */
  | {
      readonly kind: "create";
      readonly game: string;
      readonly settings: TableSettings;
      readonly seats?: number;
    }
  /** Join a table by id (an accepted invite). */
  | {
      readonly kind: "join";
      readonly game: string;
      readonly roomId: string;
      readonly preferredSeat?: number;
    }
  /** Resume a persisted online chess game. */
  | { readonly kind: "resume"; readonly game: string; readonly onlineGameId: string }
  /** Re-enter ANOTHER live table with a token (a seat-fill invite). */
  | {
      readonly kind: "rejoin";
      readonly game: string;
      readonly roomId: string;
      readonly token: string;
    }
  /** Go to the lobby — the table's own «خروج» destination, when the refusal's «خروج» routed through the
   *  held table to give the seat up (card-9f). */
  | { readonly kind: "lobby" };

/** The default join descriptor — public quick-match. */
export const QUICK_MATCH: OnlineJoin = { kind: "quick" };

/** The bot whose Mini App hosts Bing Games (for deep-link invites). The literal
 * is only the pre-boot fallback — `loadGames` overwrites it with the server's
 * `botUsername` (env `BOT_USERNAME`) so a bot rename can't silently break links. */
export const BOT_USERNAME = "BingGames_Bot";
let botUsername: string = BOT_USERNAME;

/** Called by the store once `GET /games` reports the deployment's real bot.
 * Tolerates a missing field (an older api during a deploy window) — the
 * fallback literal stays. */
export function setBotUsername(name: string | undefined): void {
  if (typeof name === "string" && name.trim()) botUsername = name.trim();
}

/** Test/inspection seam: the bot username invite links are currently built with. */
export function getBotUsername(): string {
  return botUsername;
}

/**
 * Encodes a private-table invite payload as a Telegram `startapp` code. The game
 * id is embedded (`t_<game>_<roomId>`) so a joiner can open the right container
 * without a round-trip to the server.
 */
export function tableCode(gameId: string, roomId: string): string {
  return `t_${gameId}_${roomId}`;
}

/** Parses a `t_<game>_<roomId>` start param back into its parts (or `null`). */
export function parseTableCode(
  code: string | null | undefined,
): { gameId: string; roomId: string } | null {
  if (!code) return null;
  const match = /^t_([a-z]+)_(.+)$/.exec(code);
  if (!match) return null;
  const [, gameId, roomId] = match;
  if (!gameId || !roomId) return null;
  return { gameId, roomId };
}

/** The shareable Telegram deep-link a host sends friends to join their table.
 * Bot-start fallback (no Mini App short name configured): opens the bot, whose
 * `/start` handler then opens the Mini App straight into this table (it forwards
 * the code as `?startapp=`, which the launch URL reader picks up). */
export function inviteLink(gameId: string, roomId: string): string {
  return `https://t.me/${botUsername}?start=${tableCode(gameId, roomId)}`;
}
